import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { preventBodyScroll, createButtonHandler } from '../utils/modalUtils';
import './Navbar.css';

const Navbar = () => {
  const { user, logout, isFounder, isSponsor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userPoints, setUserPoints] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    if (user) {
      fetchUserPoints();
    }
  }, [user]);

  // Close menu when route changes
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  // Prevent body scroll when menu is open on mobile (enhanced)
  useEffect(() => {
    if (isMenuOpen && window.innerWidth <= 768) {
      preventBodyScroll(true);
    } else {
      preventBodyScroll(false);
    }
    return () => {
      preventBodyScroll(false);
    };
  }, [isMenuOpen]);

  const fetchUserPoints = async () => {
    try {
      const response = await api.get('/my-points');
      setUserPoints(response.data.points);
    } catch (error) {
      console.error('Error fetching points:', error);
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMenu}>
          <img src="/logo.png" alt="ALIENS" className="navbar-logo-img" />
        </Link>
        <button 
          className={`navbar-toggle ${isMenuOpen ? 'hidden' : ''}`} 
          {...createButtonHandler(toggleMenu)}
          aria-label="Toggle menu"
        >
          <span className={isMenuOpen ? 'hamburger open' : 'hamburger'}></span>
          <span className={isMenuOpen ? 'hamburger open' : 'hamburger'}></span>
          <span className={isMenuOpen ? 'hamburger open' : 'hamburger'}></span>
        </button>
        <div className={`navbar-menu ${isMenuOpen ? 'active' : ''}`}>
          <button 
            className="navbar-close" 
            {...createButtonHandler(closeMenu)}
            aria-label="Close menu"
          >
            <span className="close-icon">×</span>
          </button>
          
          {/* Events - protected */}
          {user ? (
            <Link to="/events" className="navbar-link" onClick={closeMenu}>
              Events
            </Link>
          ) : (
            <Link to="/login" className="navbar-link navbar-link-locked" onClick={closeMenu} title="Login to access Events">
              🔒 Events
            </Link>
          )}
          
          {/* Discount Places - protected */}
          {user ? (
            <Link to="/discount-places" className="navbar-link" onClick={closeMenu}>
              Discount Places
            </Link>
          ) : (
            <Link to="/login" className="navbar-link navbar-link-locked" onClick={closeMenu} title="Login to access Discount Places">
              🔒 Discounts
            </Link>
          )}
          
          {/* Public pages */}
          <Link to="/media" className="navbar-link" onClick={closeMenu}>
            Media
          </Link>
          <Link to="/aliens-cars" className="navbar-link" onClick={closeMenu}>
            Aliens Cars
          </Link>
          <Link to="/aliens-bikes" className="navbar-link" onClick={closeMenu}>
            Aliens Bikes
          </Link>
          <Link to="/photographers" className="navbar-link" onClick={closeMenu}>
            Photographers
          </Link>
          <Link to="/shop" className="navbar-link" onClick={closeMenu}>
            Shop
          </Link>
          <Link to="/giveaways" className="navbar-link" onClick={closeMenu}>
            Giveaways
          </Link>
          {isFounder && (
            <Link to="/admin" className="btn-admin" onClick={closeMenu}>
              📊 Admin Dashboard
            </Link>
          )}
          {isSponsor && (
            <Link to="/sponsor" className="btn-admin" onClick={closeMenu}>
              📢 My Places
            </Link>
          )}
          {user ? (
            <div className="navbar-user">
              <div className="user-points-badge">
                <span className="points-icon">⭐</span>
                <span className="points-value">{userPoints}</span>
              </div>
              <span className="user-name-text">{user.name}</span>
              <button onClick={handleLogout} className="btn-logout">
                Logout
              </button>
            </div>
          ) : (
            <div className="navbar-auth">
              <Link to="/login" className="btn-login" onClick={closeMenu}>
                Login
              </Link>
              <Link to="/register" className="btn-register" onClick={closeMenu}>
                Register
              </Link>
            </div>
          )}
        </div>
      </div>
      <div 
        className={`navbar-overlay ${isMenuOpen ? 'active' : ''}`} 
        {...createButtonHandler(toggleMenu)}
      ></div>
    </nav>
  );
};

export default Navbar;
